import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import { FIELD_HINT_CLASS, SECTION_HEADING_CLASS } from 'Shared/editor/field';
import { ActionTooltip, DragHandle, LIST_ROW_CLASS, RemoveButton } from 'Shared/editor/list_row';
import { SuggestInput } from 'Shared/editor/SuggestInput';
import { movedItem, useDragReorder } from 'Shared/editor/use_drag_reorder';

/** A flag definition from the shared `flags` table, serialized by PHP. */
export type FlagDefinition = {
   flagid: string;
   title: string;
   text: string;
   icon: string | null;
   imageUrl: string | null;
};

/**
 * A document's flags, shared by the wiki and guide editors. Flags the
 * editor may add or remove carry a remove button; privileged flags already
 * on the document show read-only (with a lock) so they survive the save
 * untouched. The typeahead lists the editor's available flags.
 *
 * With `orderable`, rows drag to reorder (grip or arrow keys), like the
 * guide editor's tools and parts — the value's order is the flags' display
 * order, persisted positionally (each guide flag revision's orderby; a
 * wiki's [flag|...] token order in the wikitext).
 */
export function FlagsField({
   available: availableDefinitions,
   current,
   value,
   onChange,
   orderable = false,
   description,
}: {
   /** Flags the editor may add and remove; value entries not in here lock. */
   available: FlagDefinition[];
   /** Definitions for flags already on the document (incl. privileged). */
   current: FlagDefinition[];
   value: string[];
   onChange: (flags: string[]) => void;
   orderable?: boolean;
   /** Optional helper text under the heading, e.g. what flags do. */
   description?: string;
}) {
   const [query, setQuery] = useState('');
   const editableFlagids = new Set(availableDefinitions.map(flag => flag.flagid));
   const definitions = new Map(
      [...current, ...availableDefinitions].map(flag => [flag.flagid, flag])
   );
   const available = availableDefinitions.filter(flag => !value.includes(flag.flagid));
   const drag = useDragReorder((from, to) => onChange(movedItem(value, from, to)));

   const addFlag = (flag: FlagDefinition) => {
      if (!value.includes(flag.flagid)) {
         onChange([...value, flag.flagid]);
      }
      setQuery('');
   };

   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{_js('Flags')}</h2>
         {description && <p className={cn('mb-2', FIELD_HINT_CLASS)}>{description}</p>}
         {orderable && (
            <span aria-live="polite" className="sr-only">
               {drag.announcement}
            </span>
         )}
         <div className="flex flex-col gap-2">
            {value.length > 0 && (
               <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {drag.displayItems(value).map(({ item: flagid }, displayIndex) => {
                     const flag = definitions.get(flagid);
                     const title = flag?.title ?? flagid;
                     return (
                        <li
                           key={flagid}
                           {...(orderable ? drag.rowProps(displayIndex) : {})}
                           className={cn(
                              LIST_ROW_CLASS,
                              'group gap-3',
                              drag.draggingIndex === displayIndex &&
                                 'opacity-60 ring-2 ring-brand-300'
                           )}
                        >
                           {orderable && (
                              <DragHandle
                                 {...drag.handleProps(displayIndex, {
                                    label: _js('Reorder %1 (use the arrow keys to move it)', title),
                                 })}
                              />
                           )}
                           {flag?.icon ? (
                              <FaIcon name={flag.icon} className="size-6 shrink-0 text-gray-500" />
                           ) : flag?.imageUrl ? (
                              <img
                                 src={flag.imageUrl}
                                 alt=""
                                 className="size-6 shrink-0 object-contain"
                              />
                           ) : (
                              <FaIcon name="flag" className="size-5 shrink-0 text-gray-400" />
                           )}
                           <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-gray-900">
                                 {title}
                              </span>
                              {flag?.text && (
                                 <span className="block text-xs text-gray-500">{flag.text}</span>
                              )}
                           </span>
                           {editableFlagids.has(flagid) ? (
                              <RemoveButton
                                 label={_js('Remove %1', title)}
                                 onClick={() =>
                                    onChange(value.filter(current => current !== flagid))
                                 }
                                 testId="flag-remove"
                              />
                           ) : (
                              // Same size-7 slot as the remove button, so rows keep
                              // their grid alignment. The title attribute this
                              // replaced was invisible on touch and unreliable for
                              // screen readers.
                              <ActionTooltip label={_js('This flag cannot be removed from here.')}>
                                 <span
                                    tabIndex={0}
                                    className="flex size-7 shrink-0 items-center justify-center"
                                 >
                                    <FaIcon
                                       name="lock"
                                       className="size-3.5 text-gray-500"
                                       aria-hidden
                                    />
                                    <span className="sr-only">
                                       {_js('This flag cannot be removed from here.')}
                                    </span>
                                 </span>
                              </ActionTooltip>
                           )}
                        </li>
                     );
                  })}
               </ul>
            )}
            {available.length > 0 ? (
               <SuggestInput
                  queryKey="editor-flag-suggest"
                  value={query}
                  onValueChange={setQuery}
                  placeholder={_js('Click to list available flags...')}
                  ariaLabel={_js('Add a flag')}
                  inputTestId="flag-input"
                  minQueryLength={0}
                  searchKey={value.join(',')}
                  excludeKeys={value}
                  emptyMessage={_js('No matching flags')}
                  fetchSuggestions={async search => {
                     const normalized = search.trim().toLowerCase();
                     return available
                        .filter(flag => flag.title.toLowerCase().includes(normalized))
                        .map(flag => ({
                           key: flag.flagid,
                           label: flag.title,
                           imageUrl: flag.imageUrl,
                        }));
                  }}
                  onSelect={suggestion => {
                     const flag = availableDefinitions.find(
                        option => option.flagid === suggestion.key
                     );
                     if (flag) {
                        addFlag(flag);
                     }
                  }}
                  // Flag artwork is a square SVG, not a photo out of the
                  // image pipeline: the 4:3 frame the other pickers use would
                  // cut a quarter of its height off.
                  imageShape="icon"
                  onKeyDown={event => {
                     if (event.key !== 'Enter') {
                        return;
                     }
                     const normalized = query.trim().toLowerCase();
                     const flag = available.find(
                        option => option.title.toLowerCase() === normalized
                     );
                     if (flag) {
                        event.preventDefault();
                        addFlag(flag);
                     }
                  }}
               />
            ) : (
               // An input promising a list it can't show reads as broken; say
               // why there's nothing to add instead.
               availableDefinitions.length > 0 && (
                  <p className="text-sm text-gray-500">
                     {_js('All available flags have been added.')}
                  </p>
               )
            )}
         </div>
      </div>
   );
}
